import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
    {
        category: "Generalidades y Suscripción",
        questions: [
            {
                question: "¿Qué es la IPTV y cómo funciona?",
                answer: "La IPTV (Televisión por Protocolo de Internet) es una tecnología que permite transmitir canales de televisión y contenidos de video a través de su conexión a internet, sin pasar por una antena satélite o cable tradicional. Con TVSOLA, accede instantáneamente a más de 79,000 canales y VOD simplemente conectando su dispositivo (Smart TV, Android Box, Smartphone) a internet."
            },
            {
                question: "¿Qué dispositivos son compatibles con TVSOLA?",
                answer: "Nuestro servicio IPTV es compatible con casi todos los dispositivos conectados: Smart TV (Samsung, LG, Sony, Android TV, etc.), Android Boxes (Nvidia Shield, Xiaomi Mi Box), Fire TV Stick, Apple TV, Smartphones y Tablets (iOS y Android), PC/Mac (vía VLC o Smarters Pro) y los boxes MAG."
            },
            {
                question: "¿Puedo usar mi suscripción en varias pantallas?",
                answer: "Sí, ofrecemos opciones multipantalla. Según su plan, puede ver sus programas en 1 o 2 dispositivos simultáneamente. Es ideal para compartir su suscripción con su familia o amigos."
            },
            {
                question: "¿Ofrecen una prueba gratuita antes de la compra?",
                answer: "¡Absolutamente! Estamos convencidos de la calidad de nuestro servicio. Puede solicitar una prueba gratuita de 24h para verificar la estabilidad, la calidad de imagen (4K/FHD) y la riqueza de nuestro catálogo antes de comprometerse."
            }
        ]
    },
    {
        category: "Instalación y Técnica",
        questions: [
            {
                question: "¿Cómo instalar la IPTV en mi Smart TV?",
                answer: "La instalación es muy simple. Para Smart TV Samsung o LG, recomendamos la aplicación 'IPTV Smarters Pro' o 'IBO Player'. Una vez instalada la aplicación, solo necesita ingresar las credenciales (Xtream Codes o enlace M3U) que le enviamos por correo electrónico después de su pedido."
            },
            {
                question: "¿Qué velocidad de internet es necesaria para 4K?",
                answer: "Para una experiencia fluida en 4K UHD, recomendamos una velocidad de conexión estable de al menos 25 Mbps. Para Full HD (1080p), 10 Mbps son suficientes. La fibra óptica es ideal, pero una buena conexión ADSL o 4G/5G también funciona muy bien."
            },
            {
                question: "¿Qué hacer en caso de cortes o buffering?",
                answer: "Si experimenta ralentizaciones, primero verifique su conexión a internet (reinicie su router). Asegúrese de usar un cable Ethernet si es posible en lugar de Wi-Fi. Si el problema persiste, nuestro soporte técnico está disponible 7/7 para ayudarle a optimizar su configuración."
            },
            {
                question: "¿Necesito un VPN para usar TVSOLA?",
                answer: "El uso de un VPN no es obligatorio pero puede ser recomendado si su proveedor de internet (ISP) limita su conexión durante el streaming. Nuestro servicio es compatible con todos los principales VPN del mercado (NordVPN, ExpressVPN, CyberGhost, etc.)."
            }
        ]
    },
    {
        category: "Pago y Soporte",
        questions: [
            {
                question: "¿Cuáles son los métodos de pago aceptados?",
                answer: "Aceptamos pagos seguros con Tarjeta Bancaria (Visa, Mastercard), PayPal y Criptomonedas (Bitcoin, USDT). Todas las transacciones están cifradas y seguras."
            },
            {
                question: "¿En cuánto tiempo recibo mis accesos?",
                answer: "La entrega es automatizada y casi instantánea. Recibirá sus códigos de acceso y la guía de instalación por correo electrónico en los 5 a 15 minutos siguientes a la validación de su pago."
            },
            {
                question: "¿Ofrecen una garantía de satisfacción o devolución?",
                answer: "Sí, ofrecemos una garantía de devolución de 7 días. Si encuentra problemas técnicos que nuestro soporte no puede resolver, le reembolsamos íntegramente."
            }
        ]
    }
];

const AccordionItem = ({ question, answer, isOpen, onClick }) => {
    return (
        <motion.div 
            initial={false}
            className="border-b border-white/10 last:border-none"
        >
            <button
                onClick={onClick}
                className="w-full py-6 flex items-center justify-between text-left group"
            >
                <span className={`text-lg font-medium transition-colors duration-300 ${isOpen ? 'text-yellow-400' : 'text-white group-hover:text-yellow-200'}`}>
                    {question}
                </span>
                <span className={`ml-6 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className={`w-6 h-6 ${isOpen ? 'text-yellow-400' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </span>
            </button>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                    >
                        <div className="pb-6 text-gray-400 leading-relaxed">
                            {answer}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default function FAQ() {
    const [openIndex, setOpenIndex] = useState("0-0"); // Default open first item

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <main className="relative overflow-hidden pt-8 pb-20">
            {/* Background Elements */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-yellow-400/5 rounded-full blur-[120px]"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px]"></div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 relative z-10">
                {/* Header */}
                <div className="text-center mb-20">
                    <motion.span 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="inline-block py-1 px-3 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-sm font-semibold mb-6"
                    >
                        CENTRO DE AYUDA
                    </motion.span>
                    <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-6xl font-bold text-white mb-6"
                    >
                        Preguntas <span className="text-yellow-400">Frecuentes</span>
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-gray-400 text-lg max-w-2xl mx-auto"
                    >
                        Todo lo que necesita saber sobre TVSOLA. Si no encuentra la respuesta a su pregunta, no dude en contactar nuestro soporte.
                    </motion.p>
                </div>

                {/* FAQ Sections */}
                <div className="max-w-4xl mx-auto space-y-12">
                    {faqs.map((section, sectionIndex) => (
                        <motion.div 
                            key={sectionIndex}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: sectionIndex * 0.1 }}
                            className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10"
                        >
                            <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                                <span className="w-2 h-8 bg-yellow-400 rounded-full"></span>
                                {section.category}
                            </h2>
                            <div className="space-y-2">
                                {section.questions.map((item, itemIndex) => {
                                    const index = `${sectionIndex}-${itemIndex}`;
                                    return (
                                        <AccordionItem
                                            key={index}
                                            question={item.question}
                                            answer={item.answer}
                                            isOpen={openIndex === index}
                                            onClick={() => toggleAccordion(index)}
                                        />
                                    );
                                })}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Still have questions CTA */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-center mt-20"
                >
                    <p className="text-gray-400 mb-6">¿No encuentra la respuesta que busca?</p>
                    <a href="https://wa.me/447478034690" target="_blank" rel="noopener noreferrer" className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-8 py-3 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-lg">
                        Contactar Soporte
                    </a>
                </motion.div>
            </div>
        </main>
    );
}
